import React, { type ComponentType, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import RailTooltip from './RailTooltip';
import { railFade } from './railMotion';

export interface SidebarItemProps {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Icon rail: the texts fade out, a tooltip names the entry instead. */
  collapsed: boolean;
  active?: boolean;
  /** Not available yet: lock icon, „Bald“ badge, not clickable. */
  locked?: boolean;
  /** Tooltip text in the rail; defaults to the label. */
  hint?: string;
  /** Right of the text while expanded (the onboarding check). */
  badge?: ReactNode;
  /** Stand-in for the badge in the rail: a small dot on the icon. */
  dot?: boolean;
  /** „Abmelden“ keeps its own grey instead of the navigation look. */
  tone?: 'navigation' | 'cancel';
  /** A long label ("TimeGlobe Intelligence") that would run into the item's edge: set a little smaller and tighter. */
  tightLabel?: boolean;
  onSelect?: () => void;
}

/**
 * One sidebar entry in both states. The texts stay in the DOM (only faded and
 * clipped), so the accessible name and the focus order never change.
 */
export default function SidebarItem({
  label,
  icon: Icon,
  collapsed,
  active = false,
  locked = false,
  hint,
  badge,
  dot = false,
  tone = 'navigation',
  tightLabel = false,
  onSelect,
}: SidebarItemProps) {
  const button = (
    <Button
      variant={active ? 'secondary' : 'ghost'}
      aria-current={active ? 'page' : undefined}
      disabled={locked}
      onClick={locked ? undefined : onSelect}
      className={cn(
        'w-full justify-start',
        // Rail only: clip the faded text to the pill. Expanded, a long entry
        // with its badge may overhang (mobile sheet) as it always did.
        collapsed && 'overflow-hidden',
        tone === 'cancel'
          ? 'text-cancelled-strong hover:bg-cancelled/10 hover:text-cancelled-strong'
          : 'text-base font-normal',
        active && 'bg-secondary font-medium text-primary-strong',
        locked && 'opacity-50',
      )}
    >
      <span className="relative mr-3 flex shrink-0">
        {locked ? <Lock className="h-5 w-5" data-testid="sidebar-lock" /> : <Icon className="h-5 w-5" />}
        {dot && (
          <span
            aria-hidden="true"
            data-testid="sidebar-dot"
            className={cn(
              'absolute -right-1 -top-1 h-2 w-2 rounded-full bg-brand-strong ring-2',
              active ? 'ring-secondary' : 'ring-chrome',
              railFade(!collapsed),
            )}
          />
        )}
      </span>
      <span className={cn(tightLabel && 'text-[15px] tracking-tight', railFade(collapsed))}>{label}</span>
      {locked && (
        <span className={cn('ml-auto rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground', railFade(collapsed))}>
          Bald
        </span>
      )}
      {badge && <span className={cn('ml-auto flex', railFade(collapsed))}>{badge}</span>}
    </Button>
  );

  const tooltip = hint ?? label;
  if (locked) {
    // A disabled button gets no pointer events: the wrapper carries the hover
    // (and the not-allowed cursor) without adding a stop to the focus order.
    return (
      <RailTooltip label={tooltip} enabled={collapsed}>
        <span className="block cursor-not-allowed">{button}</span>
      </RailTooltip>
    );
  }
  return (
    <RailTooltip label={tooltip} enabled={collapsed}>
      {button}
    </RailTooltip>
  );
}
